import { useCallback, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import type { FieldPlacement } from '@/types/template-fields';
import { PlacedField } from './PlacedField';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePdfPageRenderer, PDF_PLACEHOLDER_HEIGHT, PDF_MIN_SCALE, PDF_MAX_SCALE } from '@/hooks/use-pdf-page-renderer';

// Configure PDF.js worker to use local file (not CDN)
// This works with CSP policies and offline environments
if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs?v=${pdfjs.version}-legacy`;
}

interface PdfTemplateViewerProps {
  pdfUrl: string;
  placements: FieldPlacement[];
  onPlacementsChange: (placements: FieldPlacement[]) => void;
  onPlacementsChangeComplete: (placements: FieldPlacement[]) => void;
  selectedFieldId: string | null;
  onSelectField: (id: string | null) => void;
  previewMode?: boolean;
  currentPage: number;
  onCurrentPageChange: (page: number) => void;
}

export function PdfTemplateViewer({
  pdfUrl,
  placements,
  onPlacementsChange,
  onPlacementsChangeComplete,
  selectedFieldId,
  onSelectField,
  previewMode = false,
  currentPage,
  onCurrentPageChange,
}: PdfTemplateViewerProps) {
  const {
    isDesktop,
    numPages,
    scale,
    setScale,
    pageAspectRatio,
    scrollContainerRef,
    getPageRef,
    nearViewport,
    measuredHeights,
    onDocumentLoadSuccess,
    mobileRenderWidth,
    getEffectiveScale,
    pageIndices,
  } = usePdfPageRenderer({ pdfUrl, currentPage, onCurrentPageChange });

  // Create per-page drop handler
  const handleFieldMove = useCallback(
    (id: string, newX: number, newY: number) => {
      onPlacementsChange(
        placements.map((p) => (p.id === id ? { ...p, x: newX, y: newY } : p)),
      );
    },
    [placements, onPlacementsChange],
  );

  const handleFieldMoveComplete = useCallback(
    (id: string, newX: number, newY: number) => {
      onPlacementsChangeComplete(
        placements.map((p) => (p.id === id ? { ...p, x: newX, y: newY } : p)),
      );
    },
    [placements, onPlacementsChangeComplete],
  );

  const handleFieldResize = useCallback(
    (id: string, newWidth: number, newHeight: number) => {
      onPlacementsChange(
        placements.map((p) =>
          p.id === id ? { ...p, width: newWidth, height: newHeight } : p,
        ),
      );
    },
    [placements, onPlacementsChange],
  );

  const handleFieldResizeComplete = useCallback(
    (id: string, newWidth: number, newHeight: number) => {
      onPlacementsChangeComplete(
        placements.map((p) =>
          p.id === id ? { ...p, width: newWidth, height: newHeight } : p,
        ),
      );
    },
    [placements, onPlacementsChangeComplete],
  );

  const handleDeleteField = useCallback(
    (id: string) => {
      onPlacementsChangeComplete(placements.filter((p) => p.id !== id));
      if (selectedFieldId === id) onSelectField(null);
    },
    [placements, onPlacementsChangeComplete, selectedFieldId, onSelectField],
  );

  const handleFieldUpdate = useCallback(
    (updated: FieldPlacement) => {
      onPlacementsChangeComplete(
        placements.map((p) => (p.id === updated.id ? updated : p)),
      );
    },
    [placements, onPlacementsChangeComplete],
  );

  // Group placements by page index for efficient lookup
  const placementsByPage = useMemo(() => {
    const map = new Map<number, FieldPlacement[]>();
    for (const p of placements) {
      const arr = map.get(p.page) ?? [];
      arr.push(p);
      map.set(p.page, arr);
    }
    return map;
  }, [placements]);

  const totalFields = placements.length;

  // Determine which pages should render the expensive <Page> canvas.
  // A page renders if it's near the viewport OR contains the selected field.
  const selectedFieldPage = selectedFieldId
    ? placements.find((p) => p.id === selectedFieldId)?.page
    : undefined;
  const shouldRenderPage = useCallback(
    (pageIndex: number) =>
      nearViewport.has(pageIndex) || pageIndex === selectedFieldPage,
    [nearViewport, selectedFieldPage],
  );

  return (
    <div className="flex-1 flex flex-col bg-muted/50 min-w-0">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b bg-background px-3 py-1.5 sm:flex-nowrap sm:gap-0 sm:px-4 sm:py-2">
        <span className="text-xs text-muted-foreground sm:text-sm">
          Page {currentPage} of {numPages}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setScale((s) => Math.max(PDF_MIN_SCALE, +(s - 0.1).toFixed(1)))}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="w-12 text-center text-xs text-muted-foreground sm:text-sm">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setScale((s) => Math.min(PDF_MAX_SCALE, +(s + 0.1).toFixed(1)))}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
        <div className="hidden text-xs text-muted-foreground sm:block">
          {totalFields} field(s) total
        </div>
      </div>

      {/* Scrollable PDF Canvas — all pages stacked */}
      <div
        ref={scrollContainerRef}
        className="flex flex-1 flex-col items-center gap-4 overflow-auto p-3 sm:gap-6 sm:p-8"
        onClick={() => onSelectField(null)}
      >
        <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess}>
          {pageIndices.map((pageIndex) => {
            const pagePlacements = placementsByPage.get(pageIndex) ?? [];
            const isRendered = shouldRenderPage(pageIndex);
            return (
              <div
                key={pageIndex}
                data-page-index={pageIndex}
                ref={(el) => {
                  const ref = getPageRef(pageIndex);
                  (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
                  // Record the rendered height for placeholder sizing
                  if (el && el.offsetHeight > 0) {
                    measuredHeights.current.set(pageIndex, el.offsetHeight);
                  }
                }}
                className="relative shadow-lg mb-6"
                style={
                  // When off-screen, use a fixed-height placeholder to preserve scroll position
                  !isRendered
                      ? {
                          height:
                            measuredHeights.current.get(pageIndex) ??
                            (isDesktop
                              ? PDF_PLACEHOLDER_HEIGHT * scale
                              : Math.floor(mobileRenderWidth * pageAspectRatio)),
                          width: isDesktop ? '100%' : mobileRenderWidth,
                        }
                    : undefined
                }
                onClick={(e) => e.stopPropagation()}
              >
                {isRendered ? (
                  <>
                    <Page
                      pageNumber={pageIndex + 1}
                      width={isDesktop ? undefined : mobileRenderWidth}
                      scale={isDesktop ? scale : undefined}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />

                    {/* Page label */}
                    <div className="absolute top-0 right-0 bg-black/50 text-white text-xs px-2 py-0.5 rounded-bl pointer-events-none z-[15]">
                      Page {pageIndex + 1}
                    </div>

                    {/* Placed Fields Overlay for this page */}
                    {pagePlacements.map((placement) => (
                      <PlacedField
                        key={placement.id}
                        placement={placement}
                        isSelected={selectedFieldId === placement.id}
                        onSelect={() => onSelectField(placement.id)}
                        onMove={(newX, newY) =>
                          handleFieldMove(placement.id, newX, newY)
                        }
                        onMoveComplete={(newX, newY) =>
                          handleFieldMoveComplete(placement.id, newX, newY)
                        }
                        onResize={(newW, newH) =>
                          handleFieldResize(placement.id, newW, newH)
                        }
                        onResizeComplete={(newW, newH) =>
                          handleFieldResizeComplete(placement.id, newW, newH)
                        }
                        onUpdate={handleFieldUpdate}
                        onDelete={() => handleDeleteField(placement.id)}
                        previewMode={previewMode}
                        containerRef={getPageRef(pageIndex)}
                        scale={getEffectiveScale(pageIndex)}
                      />
                    ))}
                  </>
                ) : (
                  /* Lightweight placeholder — preserves scroll height without
                     mounting a heavy PDF canvas or field overlays */
                  <div className="flex items-center justify-center h-full bg-muted/30 rounded text-muted-foreground text-sm">
                    Page {pageIndex + 1}
                  </div>
                )}
              </div>
            );
          })}
        </Document>
      </div>
    </div>
  );
}
